import { useMemo, type ReactNode } from "react";
import {
  CurrencyContext,
  type CurrencyContextType,
} from "@/shared/lib/currency/CurrencyContext";
import { useEditorContext } from "@/entities/editor-context";

/**
 * Minimal CurrencyContext for the Studio (decision D4). Keep-list components
 * read only `.currency` (preview price formatting). The real store currency
 * comes from GET /context's flat `store` block (backend P1, 2026-07-31);
 * NPR is the fallback while the "context" capability is off or the query
 * hasn't resolved.
 *
 * Must mount INSIDE EditorAuthProvider: its /context query may only fire
 * once the auth gate has a token path — above the gate, a hostless direct
 * visit would fire a doomed request and latch the session-expired state.
 */
export function StudioCurrencyProvider({ children }: { children: ReactNode }) {
  const editorContext = useEditorContext();
  const currency = editorContext.data?.store?.currency ?? "NPR";
  const value = useMemo<CurrencyContextType>(
    () => ({
      storeId: new URLSearchParams(window.location.search).get("storeId"),
      currency,
      language: "en",
      isHydrated: true,
      isSyncing: false,
      setCurrency: () => {},
      setLanguage: () => {},
    }),
    [currency],
  );
  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}
